// Sign in with Apple (web). Encapsulates Apple's "Sign in with Apple JS" SDK:
// lazy-loads it, initializes it once, and runs the popup flow to obtain the
// identity token (a JWT) that the backend verifies at POST /auth/apple.
// Apple provisions on first sign-in and returns the same `sub` afterwards, so a
// single flow covers both sign up and sign in.

const SDK_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const SDK_SCRIPT_ID = "apple-signin-sdk";
const SCOPE = "name email";
const SDK_LOAD_ERROR = "Failed to load the Sign in with Apple SDK.";
const NOT_PREPARED = "Sign in with Apple is still loading. Please try again.";

interface AppleSignInResponse {
  authorization: { id_token: string };
}

interface AppleAuth {
  init(config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    usePopup: boolean;
    nonce: string;
  }): void;
  signIn(): Promise<AppleSignInResponse>;
}

/** The token plus the raw nonce it is bound to, both sent to the backend. */
export interface AppleSignInResult {
  identityToken: string;
  /** Raw nonce; the backend checks the token's hashed `nonce` claim against it. */
  nonce: string;
}

/** Lowercase hex SHA-256 of `input` (the form Apple echoes in the token claim). */
async function sha256Hex(input: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

declare global {
  interface Window {
    AppleID?: { auth: AppleAuth };
  }
}

export class AppleSignIn {
  // The SDK is preloaded and a nonce pre-hashed by `prepare()` so the click
  // handler can open the popup with NO preceding `await`. Safari only allows
  // window.open synchronously inside the user gesture; the async gap of loading
  // the SDK or hashing the nonce made it block the popup — and a manually
  // reopened popup isn't the window the SDK listens to, so the flow hung after
  // biometric auth. Chrome tolerates the gap, which is why this only broke in
  // Safari.
  private prepared: { raw: string; hashed: string } | null = null;

  constructor(
    private readonly clientId: string,
    private readonly redirectUri: string,
  ) {}

  /** True when a Services ID is configured; the button hides otherwise. */
  get isConfigured(): boolean {
    return this.clientId.length > 0;
  }

  /**
   * Load the SDK and mint + hash a fresh nonce ahead of the click, so
   * `identityToken` can open the popup synchronously. Call from a mount effect;
   * safe to call repeatedly (each call readies a new nonce).
   */
  async prepare(): Promise<void> {
    if (!this.isConfigured) return;
    await this.loadSdk();
    const raw = crypto.randomUUID();
    this.prepared = { raw, hashed: await sha256Hex(raw) };
  }

  /**
   * Opens Apple's popup flow and resolves with the identity token plus the raw
   * nonce it is bound to (the backend verifies the token's hashed `nonce` claim
   * against it — audit M1). MUST run with no `await` before `signIn()` so the
   * popup opens inside the user gesture (Safari popup-blocker requirement);
   * `prepare()` supplies the preloaded SDK and pre-hashed nonce.
   */
  identityToken(): Promise<AppleSignInResult> {
    if (!window.AppleID || !this.prepared) {
      return Promise.reject(new Error(NOT_PREPARED));
    }
    const { raw, hashed } = this.prepared;
    // Re-init per attempt so each sign-in carries its own nonce.
    window.AppleID.auth.init({
      clientId: this.clientId,
      scope: SCOPE,
      redirectURI: this.redirectUri,
      usePopup: true,
      nonce: hashed,
    });
    const signIn = window.AppleID.auth.signIn(); // opens the popup synchronously
    this.prepared = null;
    void this.prepare(); // ready a fresh nonce for the next attempt
    return signIn.then((response) => ({
      identityToken: response.authorization.id_token,
      nonce: raw,
    }));
  }

  private loadSdk(): Promise<void> {
    if (window.AppleID) return Promise.resolve();
    const existing = document.getElementById(SDK_SCRIPT_ID);
    if (existing) return this.waitForScript(existing as HTMLScriptElement);
    const script = document.createElement("script");
    script.id = SDK_SCRIPT_ID;
    script.src = SDK_SRC;
    script.async = true;
    document.head.appendChild(script);
    return this.waitForScript(script);
  }

  private waitForScript(script: HTMLScriptElement): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.AppleID) return resolve();
      script.addEventListener("load", () => resolve());
      script.addEventListener("error", () => reject(new Error(SDK_LOAD_ERROR)));
    });
  }
}
